/* eslint-disable jsx-a11y/no-noninteractive-tabindex, jsx-a11y/no-noninteractive-element-interactions */
import type {
    Dispatch,
    FC,
    KeyboardEvent,
    MouseEvent,
    PointerEvent,
    ReactElement,
    ReactNode,
    RefObject,
    SetStateAction
} from "react"
import { useCallback, useMemo, useRef, useState } from "react"

import {
    MotionValue,
    type Transition,
    animate,
    motion,
    useMotionValue,
    useMotionValueEvent,
    useReducedMotion
} from "framer-motion"
import eq from "lodash-es/eq"
import find from "lodash-es/find"
import first from "lodash-es/first"
import includes from "lodash-es/includes"
import isUndefined from "lodash-es/isUndefined"
import last from "lodash-es/last"
import map from "lodash-es/map"
import { Trans } from "react-i18next"
import { useBoolean, useCss, useEvent, useMeasure, useMount, useUpdateEffect } from "react-use"

import { ETypographyColor, ETypographySize, ETypographyWeight, Typography } from "@/3514/components"
import {
    type TScreenBreakpoints,
    type TUseInteractionState,
    useInteractionState,
    useScreenBreakpoints
} from "@/3514/hooks"
import { THEME } from "@/3514/theme"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { type TEmptyCallback } from "@/shared/types/functions"

import { coachingChatUiConfig as uiConfig } from "../../config"
import { useCoachingChatMatrixFeedbackModalContext as useModalContext } from "../../context"
import { coachingChatStyles as chatStyles } from "../../styles"
import {
    ECoachingChatMatrixFeedbackModalChartQuadrantKey as EQuadrantKey,
    type TCoachingChatMatrixFeedbackModalChartDimensionConfig as TConfig,
    type TCoachingChatMatrixFeedbackModalChartDiscreteCoords as TDiscreteCoords,
    type TCoachingChatMatrixFeedbackModalChartDragSession as TDragSessionRef,
    type TCoachingChatMatrixFeedbackModalChartGridCell as TGridCell,
    type TCoachingChatMatrixFeedbackModalChartGridRect as TGridRect
} from "../../types"
import { coachingChatUiUtils as uiUtils } from "../../utils"

const {
    matrixFeedbackUtils: {
        createGridRect,
        createGridCells,
        clampValue,
        findNearestCellIdToCenter,
        findCellByCoordinates,
        mapCellToDiscreteCoords,
        getQuadrantKeyAtPosition
    }
} = uiUtils

const {
    matrixFeedbackChartConfig: { getSpringTransition, getLabels, getArrows, getDimensions }
} = uiConfig

const {
    modals: {
        matrix: {
            modalBodyChartWrapper: wrapperClassName,
            modalBodyContainerMessageTextInsertion: bannerInsertionClasses,
            modalBodyWrapperChart: classes
        }
    }
} = chatStyles

const Chart: FC = (): ReactElement => {
    const {
        chart: { onValueChanged }
    }: ReturnType<typeof useModalContext> = useModalContext()

    const bannerTextCss: string = useCss(bannerInsertionClasses)
    const { t }: IUseTranslation = useTranslation()
    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const {
        isActionAffected: isDraggerActionAffected,
        actions: { onMouseEnter: onDraggerMouseEnter, onMouseLeave: onDraggerMouseLeave }
    }: TUseInteractionState = useInteractionState()

    const isMotionReduced: boolean = useReducedMotion()

    const CONFIG: TConfig = useMemo((): TConfig => getDimensions(breakpoints), [breakpoints])

    const SPRING_TRANSITION: Transition = getSpringTransition({ isMotionReduced })

    const [measureRef, { width: containerWidth = 0, height: containerHeight = 0 }] = useMeasure<HTMLDivElement>()
    const containerRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement | null>(null)

    // pointer drag (without Framer drag)
    const dragSessionRef: RefObject<TDragSessionRef> = useRef<TDragSessionRef>({
        startOffsetX: 0,
        startOffsetY: 0,
        pointerDownClientX: 0,
        pointerDownClientY: 0
    })

    // offsets of the center of the bullet relative to the center of graphics
    const bulletCenterOffsetX: MotionValue<number> = useMotionValue<number>(0)
    const bulletCenterOffsetY: MotionValue<number> = useMotionValue<number>(0)

    const gridRect: TGridRect = useMemo(
        (): TGridRect => createGridRect({ containerHeight, containerWidth, gridSize: CONFIG.gridSize }),
        [containerWidth, containerHeight, CONFIG.gridSize]
    )

    const gridCells: TGridCell[] = useMemo(
        (): TGridCell[] => createGridCells({ gridRect, quadrantSize: CONFIG.quadrantSize, cellSize: CONFIG.cellSize }),
        [gridRect, CONFIG.quadrantSize, CONFIG.cellSize]
    )

    const [highlightedCellId, setHighlightedCellId]: [string, Dispatch<SetStateAction<string>>] = useState<
        string | null
    >(null)
    const [cellIdUnderBullet, setCellIdUnderBullet]: [string, Dispatch<SetStateAction<string>>] = useState<
        string | null
    >(null)

    const [overlayQuadrantKey, setOverlayQuadrantKey]: [EQuadrantKey, Dispatch<SetStateAction<EQuadrantKey>>] =
        useState<EQuadrantKey | null>(null)
    const overlayQuadrantKeyRef: RefObject<EQuadrantKey> = useRef<EQuadrantKey | null>(null)

    const setOverlayQuadrantKeyRaw: (key: EQuadrantKey) => void = useCallback((next: EQuadrantKey | null): void => {
        if (!eq(overlayQuadrantKeyRef.current, next)) {
            overlayQuadrantKeyRef.current = next
            setOverlayQuadrantKey(next)
        }
    }, [])

    const [isDragging, setIsDragging]: ReturnType<typeof useBoolean> = useBoolean(false)
    const [hasCommitted, setHasCommitted]: ReturnType<typeof useBoolean> = useBoolean(false)

    const [focusedCellId, setFocusedCellId]: [string | null, Dispatch<SetStateAction<string | null>>] = useState(null)
    const cellRefs: RefObject<Record<string, HTMLDivElement | null>> = useRef<Record<string, HTMLDivElement | null>>({})

    // bullet center travel limits
    const movementBoundsFromCenter: { maxX: number; maxY: number } = useMemo(
        (): { maxX: number; maxY: number } => ({
            maxX: CONFIG.gridSize / 2 - CONFIG.bulletSize / 2,
            maxY: CONFIG.gridSize / 2 - CONFIG.bulletSize / 2
        }),
        [CONFIG.gridSize, CONFIG.bulletSize]
    )

    // highlighting is enabled after first set
    const activeCell: TGridCell | null = useMemo(
        (): TGridCell =>
            find<TGridCell>(gridCells, (c: TGridCell): boolean =>
                eq(c.id, hasCommitted ? (highlightedCellId ?? cellIdUnderBullet) : null)
            ) ?? null,
        [cellIdUnderBullet, gridCells, hasCommitted, highlightedCellId]
    )

    const coordsCacheRef: RefObject<WeakMap<TGridCell, TDiscreteCoords>> = useRef(
        new WeakMap<TGridCell, TDiscreteCoords>()
    )

    const getCoordsByCell: (cell: TGridCell) => TDiscreteCoords = useCallback((cell: TGridCell): TDiscreteCoords => {
        const cached: TDiscreteCoords = coordsCacheRef.current.get(cell)

        if (cached) return cached

        const next: TDiscreteCoords = mapCellToDiscreteCoords(cell)

        coordsCacheRef.current.set(cell, next)

        return next
    }, [])

    const getNearestIdToBullet: () => string = useCallback((): string | null => {
        if (!gridRect || !gridCells.length) return null

        const absX: number = gridRect.centerX + bulletCenterOffsetX.get()
        const absY: number = gridRect.centerY + bulletCenterOffsetY.get()

        return findNearestCellIdToCenter(gridCells, absX, absY)
    }, [gridRect, gridCells, bulletCenterOffsetX, bulletCenterOffsetY])

    const commitToCell: (cell: TGridCell) => void = useCallback(
        (cell: TGridCell): void => {
            if (!gridRect) return

            setHasCommitted(true)

            animate<number>(bulletCenterOffsetX, cell.centerX - gridRect.centerX, SPRING_TRANSITION)
            animate<number>(bulletCenterOffsetY, cell.centerY - gridRect.centerY, SPRING_TRANSITION)

            setHighlightedCellId((prev: string): string => (eq(prev, cell.id) ? prev : cell.id))

            setFocusedCellId(cell.id)

            const coords: TDiscreteCoords = getCoordsByCell(cell)

            onValueChanged(coords)
        },
        [
            gridRect,
            setHasCommitted,
            bulletCenterOffsetX,
            SPRING_TRANSITION,
            bulletCenterOffsetY,
            getCoordsByCell,
            onValueChanged
        ]
    )

    const handleContainerClick: (e: MouseEvent) => void = useCallback(
        (e: MouseEvent): void => {
            if (!gridRect || !gridCells.length || !containerRef.current) return

            const rect: DOMRect = containerRef.current.getBoundingClientRect()
            const clickX: number = e.clientX - rect.left
            const clickY: number = e.clientY - rect.top

            // ignoring clicks outside of graphic
            if (
                clickX < gridRect.left ||
                clickX > gridRect.left + gridRect.size ||
                clickY < gridRect.top ||
                clickY > gridRect.top + gridRect.size
            )
                return

            const cell: TGridCell = findCellByCoordinates(gridCells, clickX, clickY)

            if (cell) commitToCell(cell)
        },
        [gridCells, gridRect, commitToCell]
    )

    const handleBulletPointerDown: (e: PointerEvent) => void = useCallback(
        (e: PointerEvent): void => {
            if (!gridRect) return

            if (breakpoints.xs || breakpoints.sm) e.preventDefault()
            if (!(breakpoints.sm || breakpoints.xs)) e.currentTarget.setPointerCapture(e.pointerId)

            setIsDragging(true)

            dragSessionRef.current = {
                startOffsetX: bulletCenterOffsetX.get(),
                startOffsetY: bulletCenterOffsetY.get(),
                pointerDownClientX: e.clientX,
                pointerDownClientY: e.clientY
            }

            if (cellIdUnderBullet) setHighlightedCellId(cellIdUnderBullet)
        },
        [
            gridRect,
            breakpoints.xs,
            breakpoints.sm,
            setIsDragging,
            bulletCenterOffsetX,
            bulletCenterOffsetY,
            cellIdUnderBullet
        ]
    )

    const handleBulletPointerMove: (e: PointerEvent) => void = useCallback(
        (e: PointerEvent): void => {
            if (!isDragging || !gridRect) return

            const deltaX: number = e.clientX - dragSessionRef.current.pointerDownClientX
            const deltaY: number = e.clientY - dragSessionRef.current.pointerDownClientY

            const nextOffsetX: number = clampValue(
                dragSessionRef.current.startOffsetX + deltaX,
                -movementBoundsFromCenter.maxX,
                movementBoundsFromCenter.maxX
            )
            const nextOffsetY: number = clampValue(
                dragSessionRef.current.startOffsetY + deltaY,
                -movementBoundsFromCenter.maxY,
                movementBoundsFromCenter.maxY
            )

            bulletCenterOffsetX.set(nextOffsetX)
            bulletCenterOffsetY.set(nextOffsetY)

            // preliminary illumination of the cell under the center of the bullet
            const absX: number = gridRect.centerX + nextOffsetX
            const absY: number = gridRect.centerY + nextOffsetY

            const cell: TGridCell = findCellByCoordinates(gridCells, absX, absY)

            if (cell && !eq(cell.id, highlightedCellId)) setHighlightedCellId(cell.id)
        },
        [
            isDragging,
            gridRect,
            movementBoundsFromCenter.maxX,
            movementBoundsFromCenter.maxY,
            bulletCenterOffsetX,
            bulletCenterOffsetY,
            gridCells,
            highlightedCellId
        ]
    )

    const handleBulletPointerUp: (e: PointerEvent) => void = useCallback(
        (e: PointerEvent): void => {
            if (!isDragging || !gridRect) return

            if (!(breakpoints.xs || breakpoints.sm)) e.currentTarget.releasePointerCapture(e.pointerId)

            setIsDragging(false)

            const targetId: string = highlightedCellId ?? cellIdUnderBullet

            const targetCell: TGridCell = targetId
                ? (find<TGridCell>(gridCells, (c: TGridCell): boolean => eq(c.id, targetId)) ?? null)
                : null

            if (targetCell) commitToCell(targetCell)
        },
        [
            isDragging,
            gridRect,
            breakpoints.xs,
            breakpoints.sm,
            setIsDragging,
            highlightedCellId,
            cellIdUnderBullet,
            gridCells,
            commitToCell
        ]
    )

    const handleFocusByRowAndCol: (dRow: number, dCol: number) => void = useCallback(
        (dRow: number, dCol: number): void => {
            if (!gridCells.length) return

            if (!focusedCellId && gridRect) {
                const nearestId: string = getNearestIdToBullet()

                if (nearestId) setFocusedCellId(nearestId)
                return
            }

            const currentCell: TGridCell = find<TGridCell>(gridCells, (c: TGridCell): boolean =>
                eq(c.id, focusedCellId)
            )

            if (!currentCell) return

            const nextRow: number = clampValue(currentCell.globalRow + dRow, 0, 5)
            const nextCol: number = clampValue(currentCell.globalCol + dCol, 0, 5)

            const next: TGridCell = find<TGridCell>(
                gridCells,
                (c: TGridCell): boolean => eq(c.globalRow, nextRow) && eq(c.globalCol, nextCol)
            )

            if (next) setFocusedCellId(next.id)
        },
        [gridCells, focusedCellId, getNearestIdToBullet, gridRect]
    )

    const handleContainerFocus: TEmptyCallback = useCallback((): void => {
        if (!focusedCellId) {
            const startId: string = getNearestIdToBullet()
            if (startId) setFocusedCellId(startId)
        }
    }, [focusedCellId, getNearestIdToBullet])

    const handleGridKeyDown: (e: KeyboardEvent) => void = useCallback(
        (e: KeyboardEvent): void => {
            switch (e.key) {
                case "ArrowUp":
                    e.preventDefault()
                    handleFocusByRowAndCol(-1, 0)
                    break
                case "ArrowDown":
                    e.preventDefault()
                    handleFocusByRowAndCol(+1, 0)
                    break
                case "ArrowLeft":
                    e.preventDefault()
                    handleFocusByRowAndCol(0, -1)
                    break
                case "ArrowRight":
                    e.preventDefault()
                    handleFocusByRowAndCol(0, +1)
                    break
                case "Home":
                    e.preventDefault()
                    if (gridCells.length) setFocusedCellId(first(gridCells).id)
                    break
                case "End":
                    e.preventDefault()
                    if (gridCells.length) setFocusedCellId(last(gridCells).id)
                    break
                case "Enter":
                case " ":
                    if (focusedCellId) {
                        e.preventDefault()
                        const _cell: TGridCell = find<TGridCell>(gridCells, (c: TGridCell): boolean =>
                            eq(c.id, focusedCellId)
                        )
                        if (_cell) commitToCell(_cell)
                    }
                    break
                default:
                    break
            }
        },
        [gridCells, focusedCellId, handleFocusByRowAndCol, commitToCell]
    )

    // centralized tracking: which cell is under the bullet and which quadrant should be highlighted
    const onOffsetsChange: TEmptyCallback = useCallback((): void => {
        if (!gridRect || !gridCells.length) return

        const absX: number = gridRect.centerX + bulletCenterOffsetX.get()
        const absY: number = gridRect.centerY + bulletCenterOffsetY.get()

        const cell: TGridCell = findCellByCoordinates(gridCells, absX, absY)
        const id: string = cell ? cell.id : null

        if (!eq(id, cellIdUnderBullet)) setCellIdUnderBullet(id)

        if (hasCommitted || isDragging) {
            const next: EQuadrantKey = getQuadrantKeyAtPosition(absX, absY, gridRect, overlayQuadrantKeyRef.current)

            setOverlayQuadrantKeyRaw(next)
        }
    }, [
        gridRect,
        gridCells,
        bulletCenterOffsetX,
        bulletCenterOffsetY,
        cellIdUnderBullet,
        hasCommitted,
        isDragging,
        setOverlayQuadrantKeyRaw
    ])

    useMotionValueEvent(bulletCenterOffsetX, "change", onOffsetsChange)
    useMotionValueEvent(bulletCenterOffsetY, "change", onOffsetsChange)

    // init bullet start position in geometric center, no highlighting
    useMount((): void => {
        bulletCenterOffsetX.set(0)
        bulletCenterOffsetY.set(0)
    })

    useMount((): void => {
        containerRef?.current?.focus()
    })

    useUpdateEffect((): void => {
        if (!focusedCellId) return

        cellRefs.current[focusedCellId]?.focus()
    }, [focusedCellId])

    useEvent(
        "pointermove",
        (e: Event): void => {
            if (!isDragging || breakpoints.xs || breakpoints.sm) return
            e.preventDefault?.()
            handleBulletPointerMove(e as unknown as PointerEvent)
        },
        isUndefined(window) ? undefined : window,
        { passive: false, once: true }
    )

    useEvent(
        "pointerup",
        (e: Event): void => {
            if (!isDragging || breakpoints.xs || breakpoints.sm) return
            handleBulletPointerUp(e as unknown as PointerEvent)
        },
        isUndefined(window) ? undefined : window,
        { passive: false, once: true }
    )

    useEvent(
        "pointercancel",
        (e: Event): void => {
            if (!isDragging || breakpoints.xs || breakpoints.sm) return
            handleBulletPointerUp(e as unknown as PointerEvent)
        },
        isUndefined(window) ? undefined : window,
        { passive: false, once: true }
    )

    return (
        <article className={wrapperClassName}>
            <div className={bannerTextCss}>
                <Trans
                    parent="p"
                    i18nKey="entities.coachingChat.matrixModal.chart.title"
                    components={{ strong: <strong /> }}
                />
            </div>

            <div className={classes.container()}>
                <div
                    ref={(el: HTMLDivElement): void => {
                        containerRef.current = el
                        measureRef(el)
                    }}
                    tabIndex={0}
                    role="application"
                    onFocus={handleContainerFocus}
                    onClick={handleContainerClick}
                    onKeyDown={handleGridKeyDown}
                    className={classes.wrapper()}
                    style={{ WebkitUserSelect: "none" }}
                    aria-label={t("entities.coachingChat.chat.matrixFeedback.modal.chart.grid.ariaLabel")}
                >
                    <div className={classes.overlay()} />

                    {/* quadrants */}
                    {gridRect &&
                        map<EQuadrantKey, ReactElement>(
                            [
                                EQuadrantKey.QUADRANT_1,
                                EQuadrantKey.QUADRANT_2,
                                EQuadrantKey.QUADRANT_3,
                                EQuadrantKey.QUADRANT_4
                            ],
                            (key: EQuadrantKey): ReactElement => {
                                const isRight: boolean = includes<EQuadrantKey>(
                                    [EQuadrantKey.QUADRANT_1, EQuadrantKey.QUADRANT_4],
                                    key
                                )
                                const isBottom: boolean = includes<EQuadrantKey>(
                                    [EQuadrantKey.QUADRANT_3, EQuadrantKey.QUADRANT_4],
                                    key
                                )

                                return (
                                    <motion.div
                                        key={key}
                                        className={classes.rect({ key })}
                                        animate={{ opacity: eq(overlayQuadrantKey, key) ? 1 : 0 }}
                                        initial={false}
                                        transition={{ duration: isMotionReduced ? 0 : 0.4 }}
                                        style={{
                                            left: isRight ? gridRect.left + CONFIG.quadrantSize : gridRect.left,
                                            top: isBottom ? gridRect.top + CONFIG.quadrantSize : gridRect.top,
                                            width: CONFIG.quadrantSize,
                                            height: CONFIG.quadrantSize
                                        }}
                                    />
                                )
                            }
                        )}

                    {/* cells */}
                    {map<TGridCell, ReactElement>(gridCells, (cell: TGridCell): ReactElement => {
                        const _isFocused: boolean = eq(focusedCellId, cell.id)
                        const _isSelected: boolean = hasCommitted && eq(activeCell?.id, cell.id)

                        return (
                            <div
                                key={cell.id}
                                ref={(el: HTMLDivElement): void => ((cellRefs.current[cell.id] = el), void 0)}
                                role="button"
                                onKeyDown={(e: KeyboardEvent): void => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault()
                                        commitToCell(cell)
                                    }
                                }}
                                onFocus={(): void => setFocusedCellId(cell.id)}
                                aria-label={t("entities.coachingChat.chat.matrixFeedback.modal.chart.cell.ariaLabel", {
                                    ...getCoordsByCell(cell)
                                })}
                                aria-pressed={_isSelected}
                                tabIndex={_isFocused ? -1 : 0}
                                className={classes.cell()}
                                style={{ left: cell.left, top: cell.top, width: cell.width, height: cell.height }}
                            />
                        )
                    })}

                    {/* axis */}
                    {gridRect && (
                        <div className={classes.axisContainer()} aria-hidden>
                            <div
                                className={classes.axisY()}
                                style={{ left: gridRect.centerX, top: gridRect.top, height: CONFIG.gridSize }}
                            />
                            <div
                                className={classes.axisX()}
                                style={{ top: gridRect.centerY, left: gridRect.left, width: CONFIG.gridSize }}
                            />
                        </div>
                    )}

                    {/* labels */}
                    {gridRect &&
                        map(
                            getLabels(gridRect, CONFIG.gridSize),
                            (conf): ReactElement => (
                                <div
                                    className={conf.containerClassName}
                                    style={conf.containerStyle}
                                    key={conf.labelKey}
                                >
                                    <Typography
                                        text={t(conf.labelKey)}
                                        size={ETypographySize.Tiny}
                                        weight={ETypographyWeight.Bold}
                                        color={ETypographyColor.Gray23}
                                    />
                                </div>
                            )
                        )}

                    {/* arrows */}
                    {gridRect &&
                        map<ReactNode, ReactNode>(
                            getArrows(gridRect, CONFIG.gridSize),
                            (conf: ReactNode): ReactNode => conf
                        )}

                    {/* bullet */}
                    {gridRect && (
                        <div
                            aria-hidden
                            className={classes.draggerContainer()}
                            style={{ left: gridRect.centerX, top: gridRect.centerY }}
                        >
                            <motion.button
                                type="button"
                                tabIndex={-1}
                                aria-label={t("entities.coachingChat.chat.matrixFeedback.modal.chart.button.ariaLabel")}
                                className={classes.draggerWrapper()}
                                style={{
                                    x: bulletCenterOffsetX,
                                    y: bulletCenterOffsetY,
                                    WebkitTapHighlightColor: "transparent"
                                }}
                                onPointerDown={handleBulletPointerDown}
                                onPointerMove={handleBulletPointerMove}
                                onPointerUp={handleBulletPointerUp}
                            >
                                <motion.div
                                    className={classes.dragger()}
                                    style={{ width: CONFIG.bulletSize, height: CONFIG.bulletSize }}
                                    initial={false}
                                    transition={SPRING_TRANSITION}
                                    onMouseEnter={onDraggerMouseEnter}
                                    onMouseLeave={onDraggerMouseLeave}
                                    animate={{
                                        backgroundColor:
                                            hasCommitted || isDragging || isDraggerActionAffected
                                                ? THEME.COLORS.accent
                                                : THEME.COLORS.blue[200],
                                        outlineWidth: isDragging || isDraggerActionAffected ? "8px" : "4px"
                                    }}
                                />
                            </motion.button>
                        </div>
                    )}
                </div>
            </div>
        </article>
    )
}

Chart.displayName = "CoachingChatMatrixFeedbackModalChart"

export { Chart as CoachingChatMatrixFeedbackModalChart }
